import React, { useEffect, useState } from 'react';
import { Box, Button, Modal, IconButton, Tooltip } from '@material-ui/core';
import BuzzTable from '../../../components/BuzzTable/BuzzTable';
import EditIcon from '@material-ui/icons/Edit';
import DeleteIcon from '@material-ui/icons/DeleteOutline';
import { useDispatch } from 'react-redux';
import { useSelector } from 'react-redux';
import useToast from '../../../hooks/useToast';
import useLoader from '../../../hooks/useLoader';
import useSessionStorageState from '../../../hooks/useSessionStorageState';
import CustomForm from '../../../components/FormRenderer/CustomForm';
import {
  initializeFormData,
  sanitizePayload,
} from '../../../components/FormRenderer/utils';
import CurriculumFrameworkPicker, {
  CURRICULUM_FRAMEWORK_STORAGE_KEY,
  FrameworkRequiredNotice,
} from './CurriculumFrameworkPicker';
import {
  createCurriculumRatingScale,
  getCurriculumRatingScales,
  updateCurriculumRatingScale,
} from '../AkademySlice';

/**
 * Replaces v1's "Development Grades". The difference that matters is `order`: consumers
 * are told to sort and compare by it rather than by matching on `value`, because a
 * school-defined framework can name its scale anything. `{frameworkId, order}` is unique,
 * so two scales cannot share a position.
 */
function AkademyCurriculumRatingScales() {
  const dispatch = useDispatch();
  const { ErrorToast, SuccessToast } = useToast();
  const { handleLoader } = useLoader();

  const [frameworkId, setFrameworkId] = useSessionStorageState(
    CURRICULUM_FRAMEWORK_STORAGE_KEY,
    null
  );
  const [showFormModal, setFormModal] = useState(false);
  const [selectedRowId, setSelectedRowId] = useState(null);
  const [formTemplate, setFormTemplate] = useState(null);
  const [formData, setFormData] = useState({});

  const { curriculumRatingScales } = useSelector(({ akademyReducer }) => {
    return {
      curriculumRatingScales: akademyReducer.curriculumRatingScales,
    };
  });

  useEffect(() => {
    if (!frameworkId) return;

    dispatch(getCurriculumRatingScales({ frameworkId }));
  }, [frameworkId]);

  // `frameworkId` is create-only — a scale cannot be moved between frameworks, and
  // `UpdateCurriculumRatingScaleDTO` accepts only `value` and `order`.
  const buildFormTemplate = () => [
    {
      id: 'form.value',
      title: 'Value (e.g. Secure)',
      slug: 'value',
      type: 'String',
      required: true,
    },
    {
      id: 'form.order',
      title: 'Order (1 = top of the scale)',
      slug: 'order',
      type: 'Number',
      required: true,
    },
  ];

  const ratingScaleColumns = [
    { Header: 'Rating Scale Id', accessor: '_id' },
    { Header: 'Order', accessor: 'order' },
    { Header: 'Value', accessor: 'value' },
    {
      Header: 'Action',
      Cell: (val) => {
        const { _id } = val.cell.row.original;

        return (
          <>
            <IconButton onClick={() => handleEdit(_id)}>
              <EditIcon color="secondary" />
            </IconButton>
            <Tooltip title="Curriculum has no delete — deprecate instead">
              <IconButton disabled>
                <DeleteIcon
                  color="primary"
                  style={{ color: 'grey', opacity: 0.5 }}
                />
              </IconButton>
            </Tooltip>
          </>
        );
      },
    },
  ];

  const openFormModal = () => setFormModal(true);
  const closeFormModal = () => setFormModal(false);

  const handleEdit = (id) => {
    setSelectedRowId(id);
    const { value, order } = curriculumRatingScales.filter(
      ({ _id }) => _id === id
    )[0];
    setFormTemplate(buildFormTemplate());
    setFormData({ _id: id, value, order });
    openFormModal();
  };

  const handleCreateNew = () => {
    setSelectedRowId(null);
    const template = buildFormTemplate();
    setFormTemplate(template);
    setFormData(initializeFormData(template));
    openFormModal();
  };

  const handleSave = async () => {
    handleLoader(true);

    dispatch(
      selectedRowId === null
        ? createCurriculumRatingScale(sanitizePayload({ ...formData, frameworkId }))
        : updateCurriculumRatingScale(formData)
    )
      .unwrap()
      .then(() => {
        SuccessToast(
          selectedRowId === null ? 'Rating scale created' : 'Rating scale saved'
        );
        dispatch(getCurriculumRatingScales({ frameworkId }));
        closeFormModal();
      })
      .catch((error) => {
        console.error('rejected', error);
        ErrorToast(error.message);
      });

    handleLoader(false);
  };

  return (
    <Box>
      <Box style={{ display: 'flex', gap: '2rem', alignItems: 'center' }}>
        <Button
          onClick={handleCreateNew}
          variant={'contained'}
          color="primary"
          disabled={!frameworkId}>
          Add Rating Scale
        </Button>
        <Box style={{ marginLeft: 'auto' }}>
          <CurriculumFrameworkPicker
            value={frameworkId}
            onChange={setFrameworkId}
          />
        </Box>
      </Box>

      {!frameworkId ? (
        <FrameworkRequiredNotice>
          Pick a framework to see its rating scale.
        </FrameworkRequiredNotice>
      ) : (
        <Box my={2}>
          <BuzzTable
            columns={ratingScaleColumns}
            data={curriculumRatingScales}
            isHandlePagination={true}
          />
        </Box>
      )}

      <Modal open={showFormModal} onClose={closeFormModal}>
        <CustomForm
          formFor={'Rating Scale'}
          formTemplate={formTemplate}
          formData={formData}
          setFormData={(slug, newValue) => {
            setFormData((state) => {
              state[slug] = newValue;
              return state;
            });
            setFormTemplate((state) => {
              return JSON.parse(JSON.stringify(state));
            });
          }}
          mode={selectedRowId === null ? 'CREATE' : 'EDIT'}
          handleSave={handleSave}
        />
      </Modal>
    </Box>
  );
}

export default AkademyCurriculumRatingScales;
